import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  FaArrowLeft, FaHeart, FaComments, FaShareAlt, FaCheckCircle,
  FaLock, FaCrown, FaMapMarkerAlt, FaBriefcase, FaGraduationCap,
  FaUser, FaUsers, FaRing, FaChevronLeft, FaChevronRight, FaTimes,
  FaPhoneAlt, FaEnvelope, FaRulerVertical, FaWeight, FaTint,
  FaPray, FaLanguage, FaStar, FaUserCircle
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// Photo Gallery
const PhotoGallery = ({ photos }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  if (!photos || photos.length === 0) {
    return (
      <div className="w-full h-80 md:h-[450px] bg-gradient-to-br from-[#8B0000] to-[#DC143C] rounded-2xl flex items-center justify-center">
        <FaUserCircle className="text-white/30 text-[120px]" />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {/* Main Photo */}
        <div
          className="relative w-full h-80 md:h-[450px] rounded-2xl overflow-hidden cursor-pointer group"
          onClick={() => setLightboxOpen(true)}
        >
          <img
            src={photos[activeIndex]}
            alt="Profile"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
          {photos.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setActiveIndex(i => (i - 1 + photos.length) % photos.length); }}
                className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/80 p-2 rounded-full shadow hover:bg-white transition-colors"
              >
                <FaChevronLeft className="text-[#8B0000]" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setActiveIndex(i => (i + 1) % photos.length); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/80 p-2 rounded-full shadow hover:bg-white transition-colors"
              >
                <FaChevronRight className="text-[#8B0000]" />
              </button>
            </>
          )}
          <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
            {activeIndex + 1} / {photos.length}
          </div>
        </div>

        {/* Thumbnails */}
        {photos.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {photos.map((photo, idx) => (
              <button
                key={idx}
                onClick={() => setActiveIndex(idx)}
                className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                  idx === activeIndex ? 'border-[#DC143C] ring-2 ring-[#DC143C]/30' : 'border-transparent opacity-70 hover:opacity-100'
                }`}
              >
                <img src={photo} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center" onClick={() => setLightboxOpen(false)}>
          <button className="absolute top-4 right-4 text-white text-2xl hover:text-[#FFD700]" onClick={() => setLightboxOpen(false)}>
            <FaTimes />
          </button>
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-2xl hover:text-[#FFD700]"
            onClick={(e) => { e.stopPropagation(); setActiveIndex(i => (i - 1 + photos.length) % photos.length); }}
          >
            <FaChevronLeft />
          </button>
          <img
            src={photos[activeIndex]}
            alt="Profile"
            className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
            onClick={e => e.stopPropagation()}
          />
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-2xl hover:text-[#FFD700]"
            onClick={(e) => { e.stopPropagation(); setActiveIndex(i => (i + 1) % photos.length); }}
          >
            <FaChevronRight />
          </button>
        </div>
      )}
    </>
  );
};

// Info Section Card
const InfoSection = ({ icon: Icon, title, children }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
    <div className="bg-gradient-to-r from-[#8B0000] to-[#DC143C] px-5 py-3 flex items-center gap-2">
      <Icon className="text-[#FFD700]" />
      <h3 className="text-white font-semibold font-['Playfair_Display']">{title}</h3>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

// Info Row
const InfoRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex justify-between py-2 border-b border-gray-50 last:border-0">
      <span className="text-gray-500 text-sm font-['Noto_Sans_Devanagari']">{label}</span>
      <span className="text-gray-800 text-sm font-medium text-right">{value}</span>
    </div>
  );
};

// Premium Gate
const PremiumGate = ({ children, isPremium }) => {
  if (isPremium) return <>{children}</>;
  return (
    <div className="relative">
      <div className="blur-md select-none pointer-events-none">{children}</div>
      <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-xl">
        <div className="text-center p-4">
          <FaLock className="text-[#8B0000] text-2xl mx-auto mb-2" />
          <p className="text-[#8B0000] font-bold font-['Noto_Sans_Devanagari'] mb-1">संपर्क माहिती पाहण्यासाठी</p>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 bg-[#FFD700] text-[#8B0000] px-4 py-2 rounded-full text-sm font-bold hover:bg-[#B8860B] hover:text-white transition-colors"
          >
            <FaCrown /> Premium अपग्रेड करा
          </Link>
        </div>
      </div>
    </div>
  );
};

// Skeleton
const ProfileSkeleton = () => (
  <div className="animate-pulse max-w-6xl mx-auto px-4 py-8">
    <div className="h-8 bg-gray-200 rounded w-32 mb-6" />
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1">
        <div className="h-80 bg-gray-200 rounded-2xl" />
      </div>
      <div className="lg:col-span-2 space-y-4">
        <div className="h-10 bg-gray-200 rounded w-2/3" />
        <div className="h-6 bg-gray-200 rounded w-1/3" />
        <div className="h-40 bg-gray-200 rounded-xl" />
        <div className="h-40 bg-gray-200 rounded-xl" />
      </div>
    </div>
  </div>
);

const ProfileDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isPremium, api, user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [interestSent, setInterestSent] = useState(false);
  const [sendingInterest, setSendingInterest] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/api/search/${id}`);
        setProfile(response.data.profile || response.data);
      } catch (error) {
        console.error('Failed to fetch profile:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [api, id]);

  const handleExpressInterest = async () => {
    setSendingInterest(true);
    try {
      await api.post('/api/interests/send', { profileId: id });
      setInterestSent(true);
    } catch (error) {
      console.error('Failed to send interest:', error);
    } finally {
      setSendingInterest(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: `${profile?.name} - Vivah Arambh`, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('लिंक कॉपी केली!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF8F0]">
        <Navbar />
        <ProfileSkeleton />
        <Footer />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-[#FFF8F0]">
        <Navbar />
        <div className="text-center py-20">
          <h2 className="text-2xl font-bold text-[#8B0000] font-['Noto_Sans_Devanagari'] mb-4">प्रोफाइल सापडले नाही</h2>
          <button onClick={() => navigate('/browse')} className="text-[#DC143C] hover:underline">
            ← ब्राउज पेजवर परत जा
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF8F0]">
      <Navbar />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#8B0000] hover:text-[#DC143C] mb-6 text-sm font-medium transition-colors"
        >
          <FaArrowLeft /> ब्राउज पेजवर परत जा
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Photo & Quick Actions */}
          <div className="lg:col-span-1 space-y-4">
            <PhotoGallery photos={profile.photos} />

            {/* Quick Info Card */}
            <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
              <div className="flex items-center gap-3 mb-3">
                <h2 className="text-2xl font-bold text-[#8B0000] font-['Playfair_Display']">
                  {profile.name}
                </h2>
                {profile.isVerified && (
                  <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                    <FaCheckCircle /> सत्यापित
                  </span>
                )}
              </div>
              <p className="text-gray-500 text-sm font-mono mb-3">ID: #{profile.profileId || profile._id?.slice(-8)}</p>
              <div className="space-y-2 text-sm">
                {profile.age && <p className="text-gray-600 font-['Noto_Sans_Devanagari']">{profile.age} वर्षे</p>}
                {profile.height && <p className="flex items-center gap-2 text-gray-600"><FaRulerVertical className="text-[#B8860B]" /> {profile.height}</p>}
                {profile.city && <p className="flex items-center gap-2 text-gray-600"><FaMapMarkerAlt className="text-[#DC143C]" /> {profile.city}{profile.state ? `, ${profile.state}` : ''}</p>}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2">
              {profile._id !== user?._id && (
                <>
                  <button
                    onClick={handleExpressInterest}
                    disabled={interestSent || sendingInterest}
                    className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm transition-all font-['Noto_Sans_Devanagari'] ${
                      interestSent
                        ? 'bg-green-100 text-green-700 cursor-default'
                        : 'bg-[#DC143C] text-white hover:bg-[#8B0000] shadow-lg hover:shadow-xl'
                    }`}
                  >
                    <FaHeart />
                    {interestSent ? 'रस व्यक्त केला!' : sendingInterest ? 'पाठवत आहे...' : 'रस व्यक्त करा'}
                  </button>

                  {isPremium ? (
                    <Link
                      to={`/chat/${profile._id}`}
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm bg-[#8B0000] text-white hover:bg-[#DC143C] transition-colors"
                    >
                      <FaComments /> चॅट करा
                    </Link>
                  ) : (
                    <Link
                      to="/pricing"
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm bg-gray-100 text-gray-500 border border-gray-200"
                    >
                      <FaLock /> चॅट करा (Premium)
                    </Link>
                  )}
                </>
              )}

              <button
                onClick={handleShare}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm border border-[#8B0000] text-[#8B0000] hover:bg-[#8B0000] hover:text-white transition-colors font-['Noto_Sans_Devanagari']"
              >
                <FaShareAlt /> शेअर करा
              </button>
            </div>

            {/* Contact Details - Premium Gated */}
            <PremiumGate isPremium={isPremium}>
              <InfoSection icon={FaPhoneAlt} title="Contact Details">
                <InfoRow label="मोबाईल" value={profile.mobile || profile.phone || '+91 98XXXXXXXX'} />
                <InfoRow label="ईमेल" value={profile.email || 'user@example.com'} />
                {profile.whatsapp && <InfoRow label="WhatsApp" value={profile.whatsapp} />}
              </InfoSection>
            </PremiumGate>
          </div>

          {/* Right Column - Details */}
          <div className="lg:col-span-2 space-y-5">
            {/* About */}
            {profile.about && (
              <InfoSection icon={FaUser} title="About / स्वतःबद्दल">
                <p className="text-gray-700 leading-relaxed font-['Noto_Sans_Devanagari']">{profile.about}</p>
              </InfoSection>
            )}

            {/* Personal Info */}
            <InfoSection icon={FaUser} title="Personal Information / वैयक्तिक माहिती">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                <InfoRow label="नाव" value={profile.name} />
                <InfoRow label="वय" value={profile.age ? `${profile.age} वर्षे` : null} />
                <InfoRow label="उंची" value={profile.height} />
                <InfoRow label="वजन" value={profile.weight ? `${profile.weight} kg` : null} />
                <InfoRow label="वर्ण" value={profile.complexion} />
                <InfoRow label="रक्तगट" value={profile.bloodGroup} />
                <InfoRow label="वैवाहिक स्थिती" value={profile.maritalStatus} />
                <InfoRow label="जन्मतारीख" value={profile.dateOfBirth} />
              </div>
            </InfoSection>

            {/* Community */}
            <InfoSection icon={FaPray} title="Community / समाज माहिती">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                <InfoRow label="धर्म" value={profile.religion} />
                <InfoRow label="जात" value={profile.caste} />
                <InfoRow label="उपजात" value={profile.subCaste} />
                <InfoRow label="राशी" value={profile.rashi} />
                <InfoRow label="गोत्र" value={profile.gothra} />
                <InfoRow label="मातृभाषा" value={profile.motherTongue} />
                <InfoRow label="कुलदैवत" value={profile.kuldaivat} />
                <InfoRow label="नक्षत्र" value={profile.nakshatra} />
              </div>
            </InfoSection>

            {/* Education & Career */}
            <InfoSection icon={FaGraduationCap} title="Education & Career / शिक्षण व करिअर">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                <InfoRow label="शिक्षण" value={profile.qualification} />
                <InfoRow label="व्यवसाय" value={profile.occupation} />
                <InfoRow label="वार्षिक उत्पन्न" value={profile.income ? `₹${profile.income}` : null} />
                <InfoRow label="कंपनी" value={profile.company} />
                <InfoRow label="कामाचे ठिकाण" value={profile.workCity} />
              </div>
            </InfoSection>

            {/* Location */}
            <InfoSection icon={FaMapMarkerAlt} title="Location / पत्ता">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                <InfoRow label="शहर" value={profile.city} />
                <InfoRow label="राज्य" value={profile.state} />
                <InfoRow label="सध्याचा पत्ता" value={profile.currentAddress} />
                <InfoRow label="मूळ गाव" value={profile.nativePlace} />
              </div>
            </InfoSection>

            {/* Family */}
            {(profile.fatherName || profile.fatherOccupation || profile.motherName || profile.siblings) && (
              <InfoSection icon={FaUsers} title="Family / कुटुंब माहिती">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                  <InfoRow label="वडिलांचे नाव" value={profile.fatherName} />
                  <InfoRow label="वडिलांचा व्यवसाय" value={profile.fatherOccupation} />
                  <InfoRow label="आईचे नाव" value={profile.motherName} />
                  <InfoRow label="आईचा व्यवसाय" value={profile.motherOccupation} />
                  <InfoRow label="भावंडे" value={profile.siblings} />
                  <InfoRow label="कुटुंब प्रकार" value={profile.familyType} />
                  <InfoRow label="कुटुंबाची स्थिती" value={profile.familyStatus} />
                </div>
              </InfoSection>
            )}

            {/* Partner Preferences */}
            {(profile.partnerPreferences || profile.preferences) && (
              <InfoSection icon={FaRing} title="Partner Preferences / जोडीदार अपेक्षा">
                {(() => {
                  const prefs = profile.partnerPreferences || profile.preferences || {};
                  return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                      <InfoRow label="वय" value={prefs.ageRange ? `${prefs.ageRange.min} - ${prefs.ageRange.max} वर्षे` : (prefs.ageMin ? `${prefs.ageMin} - ${prefs.ageMax} वर्षे` : null)} />
                      <InfoRow label="उंची" value={prefs.heightRange ? `${prefs.heightRange.min} - ${prefs.heightRange.max}` : (prefs.heightMin ? `${prefs.heightMin} - ${prefs.heightMax}` : null)} />
                      <InfoRow label="शिक्षण" value={Array.isArray(prefs.qualification) ? prefs.qualification.join(', ') : prefs.qualification} />
                      <InfoRow label="व्यवसाय" value={Array.isArray(prefs.occupation) ? prefs.occupation.join(', ') : prefs.occupation} />
                      <InfoRow label="जात" value={Array.isArray(prefs.caste) ? prefs.caste.join(', ') : prefs.caste} />
                      <InfoRow label="शहर" value={Array.isArray(prefs.city) ? prefs.city.join(', ') : prefs.city} />
                      <InfoRow label="वैवाहिक स्थिती" value={prefs.maritalStatus} />
                      <InfoRow label="उत्पन्न" value={prefs.incomeMin ? `₹${prefs.incomeMin} - ₹${prefs.incomeMax}` : null} />
                    </div>
                  );
                })()}
              </InfoSection>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProfileDetailPage;
